import { Injectable } from '@angular/core';
import { environment } from '../environments/environment';
import { ApplicationDetail, JobStatusResponse, SystemDetails } from '../models/application';

const TERMINAL_JOB_STATUSES = ['Completed', 'CompletedWithIssues', 'Failed', 'Interrupted'];

@Injectable({ providedIn: 'root' })
export class ServiceManagerService {

  private readonly GO_LIVE_PATH      = '/api/new-customer/application-go-live';
  private readonly ROLLBACK_PATH     = '/api/new-customer/rollback-webhook';
  private readonly VALIDATE_PATH     = '/api/new-customer/validate';
  private readonly TEMP_PASSWORD_PATH = '/api/new-customer/fleet-temp-password';
  private readonly SUSPEND_PATH      = '/api/new-customer/application-suspend';
  private readonly REINSTATE_PATH    = '/api/new-customer/application-reinstate';
  private readonly STATUS_PATH       = '/api/new-customer/status';

  private readonly POLL_INTERVAL_MS = 3000;
  private readonly POLL_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

  private get baseUrl(): string | undefined {
    return (environment as { serviceManagerUrl?: string }).serviceManagerUrl;
  }

  private get apiKey(): string | undefined {
    return (environment as { serviceManagerApiKey?: string }).serviceManagerApiKey;
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // Polls GET /api/new-customer/status/{jobId} until the job reaches a terminal state, or throws
  // after POLL_TIMEOUT_MS. The job itself keeps running server-side regardless of this timeout —
  // this only bounds how long the caller waits for a result.
  private async pollJobUntilTerminal(jobId: string): Promise<JobStatusResponse> {
    const url = this.baseUrl!;
    const apiKey = this.apiKey!;
    const deadline = Date.now() + this.POLL_TIMEOUT_MS;

    while (true) {
      const res = await fetch(`${url}${this.STATUS_PATH}/${encodeURIComponent(jobId)}`, {
        headers: { 'api-key': apiKey },
      });

      if (!res.ok) {
        throw new Error(`Status check failed: ${res.status} ${res.statusText}`);
      }

      const job = (await res.json()) as JobStatusResponse;
      if (TERMINAL_JOB_STATUSES.includes(job.status)) {
        return job;
      }

      if (Date.now() > deadline) {
        throw new Error(`Timed out waiting for job ${jobId} to complete.`);
      }

      await this.sleep(this.POLL_INTERVAL_MS);
    }
  }

  async triggerRollback(
    application: ApplicationDetail,
    applicationData: Record<string, unknown> | null
  ): Promise<JobStatusResponse> {
    const url = this.baseUrl;
    const apiKey = this.apiKey;

    if (!url || !apiKey) {
      throw new Error('Service manager URL or API key not configured.');
    }

    const payload: Record<string, unknown> = {
      application: {
        id: application.id,
        status: application.status,
        user_id: application.user_id,
        decision: application.decision ?? null,
        decision_at: application.decision_at ?? null,
        current_step: application.current_step ?? null,
        submitted_at: application.submitted_at ?? null,
        decision_notes: application.decision_notes ?? null,
        system_details_id: application.system_details_id ?? null,
      },
      application_data: applicationData ?? {},
      system_details: application.system_details ?? {},
      onboarding_documents: [],
    };

    const response = await fetch(url + this.ROLLBACK_PATH, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'api-key': apiKey,
      },
      body: JSON.stringify(payload),
    });

    if (response.status !== 202) {
      throw new Error(`Rollback request failed: ${response.status} ${response.statusText}`);
    }

    const { jobId } = (await response.json()) as { jobId: string };
    return this.pollJobUntilTerminal(jobId);
  }

  notifyApplicationGoLive(
    applicationId: string,
    applicationData: Record<string, unknown> | null,
    systemDetails: SystemDetails | null
  ): void {
    const url = this.baseUrl;
    const apiKey = this.apiKey;

    if (!url || !apiKey) {
      console.warn('Service manager URL or API key not configured; skipping go-live notification.');
      return;
    }

    const payload: Record<string, unknown> = {
      "application_data": applicationData ?? {},
      "system_details": systemDetails ?? {}
    };

    fetch(url + this.GO_LIVE_PATH, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'api-key': apiKey,
      },
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (!response.ok) {
          console.error(
            `Service manager go-live notification failed: ${response.status} ${response.statusText}`
          );
        }
      })
      .catch((error) => {
        console.error('Failed to notify service manager (go-live):', error);
      });
  }

  async notifyApplicationSuspend(
    applicationId: string,
    applicationData: Record<string, unknown> | null,
    systemDetails: SystemDetails | null
  ): Promise<JobStatusResponse> {
    const url = this.baseUrl;
    const apiKey = this.apiKey;

    if (!url || !apiKey) {
      throw new Error('Service manager URL or API key not configured.');
    }

    const payload: Record<string, unknown> = {
      "application_data": applicationData ?? {},
      "system_details": systemDetails ?? {}
    };

    const response = await fetch(url + this.SUSPEND_PATH, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'api-key': apiKey,
      },
      body: JSON.stringify(payload),
    });

    if (response.status !== 202) {
      throw new Error(`Suspend request failed: ${response.status} ${response.statusText}`);
    }

    const { jobId } = (await response.json()) as { jobId: string };
    return this.pollJobUntilTerminal(jobId);
  }

  async notifyApplicationReinstate(
    applicationId: string,
    applicationData: Record<string, unknown> | null,
    systemDetails: SystemDetails | null
  ): Promise<JobStatusResponse> {
    const url = this.baseUrl;
    const apiKey = this.apiKey;

    if (!url || !apiKey) {
      throw new Error('Service manager URL or API key not configured.');
    }

    const payload: Record<string, unknown> = {
      "application_data": applicationData ?? {},
      "system_details": systemDetails ?? {}
    };

    const response = await fetch(url + this.REINSTATE_PATH, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'api-key': apiKey,
      },
      body: JSON.stringify(payload),
    });

    if (response.status !== 202) {
      throw new Error(`Reinstate request failed: ${response.status} ${response.statusText}`);
    }

    const { jobId } = (await response.json()) as { jobId: string };
    return this.pollJobUntilTerminal(jobId);
  }

  async fetchFleetTempPassword(
    applicationData: Record<string, unknown> | null,
    systemDetails: SystemDetails | null
  ): Promise<{ username: string; tempPassword: string }> {
    const url = this.baseUrl;
    const apiKey = this.apiKey;

    if (!url || !apiKey) {
      throw new Error('Service manager URL or API key not configured.');
    }

    const payload = {
      application_data: applicationData ?? {},
      system_details: systemDetails ?? {}
    };

    const response = await fetch(url + this.TEMP_PASSWORD_PATH, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'api-key': apiKey },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch credentials: ${response.status} ${response.statusText}`);
    }

    return response.json() as Promise<{ username: string; tempPassword: string }>;
  }

  async validateCustomer(fleetPhone: string, fleetName: string): Promise<boolean> {
    const url = this.baseUrl;
    const apiKey = this.apiKey;

    if (!url || !apiKey) {
      throw new Error('Service manager URL or API key not configured.');
    }

    const response = await fetch(url + this.VALIDATE_PATH, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'api-key': apiKey,
      },
      body: JSON.stringify({ MasterPhoneNumber: fleetPhone, CustomerName: fleetName }),
    });

    if (!response.ok) {
      throw new Error(`Validation request failed: ${response.status} ${response.statusText}`);
    }

    return response.json() as Promise<boolean>;
  }
}
